import { CircleCheck, Info, Loader } from "lucide-react";

import {
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from "@/components/ui/popover";
import RuvixxDate from "components/RuvixxDate";

// Ruvixx brand teal; theme-invariant, so it is used directly rather than a token.
const TEAL = "#24B388";

const isFailed = status => status === "error" || status === "no_access";

// Turn a known Meta Graph error into a plain, calm explanation that names the
// permission the failed job needs. Unmapped messages fall back to a generic
// line, with the Meta error shown beneath as selectable text for reporting.
const explainMetaError = message => {
  if (!message) return null;
  if (/\(#190\)/.test(message)) {
    return "This token can't read the Page's lead forms. It needs a Page access token with the leads_retrieval permission.";
  }
  if (/\(#100\)/.test(message) && /permission denied/i.test(message)) {
    return "This token can't read pixel stats. It needs the ads_read permission.";
  }
  return null;
};

// The recorded error wraps the raw Graph response, e.g. "Meta Graph API request
// to <url> failed: {...\"message\":\"(#190) ...\",\"code\":190...}". Users don't
// need the endpoint URL — surface the inner Graph message (which carries the
// error code) instead, falling back to the raw text when it can't be parsed.
const metaErrorDetail = message => {
  if (!message) return null;
  const inner = message.match(/"message"\s*:\s*"((?:[^"\\]|\\.)*)"/);
  return inner ? inner[1] : message;
};

const DomainStatusIcon = ({ status }) =>
  status === "success" ? (
    <CircleCheck className="tw:size-3.5" style={{ color: TEAL }} aria-hidden />
  ) : (
    <Info
      className="tw:size-3.5 tw:text-[var(--color-text-muted)]"
      aria-hidden
    />
  );

// Clickable status indicator for a Meta sync group. An all-success group reads
// as a calm green check; a group with any failed domain reads as a muted info
// icon whose popover names each domain and explains the failure in plain
// language. A pending group shows a spinner and no popover; a never-synced
// group shows nothing.
const MetaSyncStatusPopover = ({ status, domains = [], lastSyncedAt }) => {
  if (status === "pending") {
    return (
      <Loader
        className="tw:size-3.5 tw:animate-spin tw:text-[var(--color-text-muted)]"
        aria-label="Sync in progress"
      />
    );
  }

  if (status !== "success" && status !== "error") {
    return null;
  }

  const allSynced = status === "success";

  return (
    <Popover>
      <PopoverTrigger
        aria-label="Sync status"
        className="tw:inline-flex tw:cursor-pointer tw:items-center tw:rounded-sm tw:outline-hidden focus-visible:tw:ring-1 focus-visible:tw:ring-[var(--color-border)]"
      >
        {allSynced ? (
          <CircleCheck
            data-testid="meta-sync-status-success"
            className="tw:size-3.5"
            style={{ color: TEAL }}
          />
        ) : (
          <Info
            data-testid="meta-sync-status-error"
            className="tw:size-3.5 tw:text-[var(--color-text-muted)]"
          />
        )}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="tw:w-80 tw:bg-[var(--color-surface-overlay)] tw:text-[var(--color-text-primary)]"
      >
        <PopoverHeader>
          <PopoverTitle>
            {allSynced ? "All synced" : "Sync finished with gaps"}
          </PopoverTitle>
          {lastSyncedAt && (
            <span className="tw:text-xs tw:text-[var(--color-text-secondary)]">
              Last synced{" "}
              <RuvixxDate date={lastSyncedAt} isAbsoluteDateEnabled />
            </span>
          )}
        </PopoverHeader>
        {!allSynced && (
          <ul className="tw:flex tw:flex-col tw:gap-2">
            {domains.map(domain => (
              <li key={domain.key} className="tw:flex tw:flex-col tw:gap-1">
                <span className="tw:inline-flex tw:items-center tw:gap-1.5">
                  <DomainStatusIcon status={domain.status} />
                  <span>{domain.label}</span>
                </span>
                {isFailed(domain.status) && (
                  <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-0.5 tw:pl-5">
                    <span className="tw:text-xs tw:text-[var(--color-text-secondary)]">
                      {explainMetaError(domain.message) ||
                        "This data could not be synced."}
                    </span>
                    {domain.message && (
                      <span className="tw:break-words tw:text-xs tw:text-[var(--color-text-muted)] tw:select-text">
                        {metaErrorDetail(domain.message)}
                      </span>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default MetaSyncStatusPopover;
